import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, MessageCircle, ShoppingBag } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { formatARS } from "@/lib/format";
import { waLink, orderMsg } from "@/lib/whatsapp";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";

const EMPTY_BUYER = { name: "", email: "", phone: "", address: "", city: "", notes: "" };

export default function CheckoutPage() {
  const { items, totals, clear } = useCart();
  const [buyer, setBuyer] = useState(EMPTY_BUYER);
  const [payment, setPayment] = useState("mercadopago");
  const [loading, setLoading] = useState(false);
  const [placed, setPlaced] = useState(null);

  const set = (k) => (e) => setBuyer((b) => ({ ...b, [k]: e.target.value }));

  if (placed) {
    return (
      <main className="mx-auto max-w-2xl px-5 pb-24 pt-36 sm:px-8 lg:pt-44">
        <div className="border border-volt/30 bg-panel p-8 text-center sm:p-12" data-testid="checkout-success-panel">
          <CheckCircle2 className="mx-auto h-12 w-12 text-volt" />
          <h1 className="mt-5 font-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl">
            ¡Pedido registrado!
          </h1>
          <p className="mt-2 font-mono text-sm text-zinc-400">
            Código: <span className="font-bold text-volt" data-testid="checkout-order-id">{placed.order_id}</span>
          </p>
          <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-zinc-400">
            {placed.demo_mode
              ? "Te contactamos por WhatsApp para coordinar el pago y la entrega. Si preferís, escribinos ahora con tu pedido listo."
              : "Si no fuiste redirigido a Mercado Pago, coordiná el pago por WhatsApp."}
          </p>
          <a
            href={waLink(orderMsg(placed, placed.buyer))}
            target="_blank"
            rel="noreferrer"
            data-testid="checkout-success-whatsapp-button"
            className="mt-7 inline-flex items-center gap-3 bg-wa px-8 py-4 font-display text-base font-black uppercase tracking-widest text-black transition-colors hover:bg-[#20bd5a]"
          >
            <MessageCircle className="h-5 w-5" /> Enviar pedido por WhatsApp
          </a>
          <p className="mt-6">
            <Link to="/tienda" data-testid="checkout-success-back-button" className="font-mono text-xs uppercase tracking-widest text-zinc-500 hover:text-volt">
              Seguir comprando
            </Link>
          </p>
        </div>
      </main>
    );
  }

  if (items.length === 0) {
    return (
      <main className="mx-auto flex min-h-[60vh] max-w-2xl flex-col items-center justify-center gap-4 px-5 pt-32 text-center sm:px-8">
        <ShoppingBag className="h-10 w-10 text-zinc-700" />
        <p className="font-display text-2xl font-black uppercase text-white">Tu carrito está vacío</p>
        <Link to="/tienda" data-testid="checkout-empty-catalog-button" className="bg-volt px-6 py-3 font-display font-black uppercase tracking-widest text-black">
          Ver catálogo
        </Link>
      </main>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        items: items.map((i) => ({ product_id: i.productId, size: i.size, qty: i.qty })),
        buyer,
        payment_method: payment,
      });
      if (data.checkout_url) {
        toast.success("Redirigiendo a Mercado Pago...");
        window.location.assign(data.checkout_url);
        return;
      }
      setPlaced({ ...data, buyer, items });
      clear();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-panel-2 px-4 py-3 text-sm text-white placeholder:text-zinc-600 outline-none focus:border-volt transition-colors";

  return (
    <main className="mx-auto max-w-7xl px-5 pb-24 pt-32 sm:px-8 lg:pt-40">
      <Reveal>
        <p className="font-mono text-[11px] uppercase tracking-widest text-volt">Último paso</p>
        <h1 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">Checkout</h1>
      </Reveal>

      <form onSubmit={submit} className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <fieldset className="border border-white/10 bg-panel p-6 sm:p-8">
            <legend className="px-2 font-mono text-[11px] uppercase tracking-widest text-volt">Tus datos</legend>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input required data-testid="checkout-input-name" value={buyer.name} onChange={set("name")} placeholder="Nombre y apellido *" className={inputCls} />
              <input required type="email" data-testid="checkout-input-email" value={buyer.email} onChange={set("email")} placeholder="Email *" className={inputCls} />
              <input required data-testid="checkout-input-phone" value={buyer.phone} onChange={set("phone")} placeholder="Teléfono / WhatsApp *" className={inputCls} />
              <input required data-testid="checkout-input-city" value={buyer.city} onChange={set("city")} placeholder="Localidad *" className={inputCls} />
              <input required data-testid="checkout-input-address" value={buyer.address} onChange={set("address")} placeholder="Dirección de entrega *" className={`${inputCls} sm:col-span-2`} />
              <textarea data-testid="checkout-input-notes" value={buyer.notes} onChange={set("notes")} placeholder="Notas (horario, referencia, talle especial...)" rows={3} className={`${inputCls} sm:col-span-2`} />
            </div>
          </fieldset>

          <fieldset className="mt-6 border border-white/10 bg-panel p-6 sm:p-8">
            <legend className="px-2 font-mono text-[11px] uppercase tracking-widest text-volt">Método de pago</legend>
            <div className="space-y-3">
              <label className={`flex cursor-pointer items-center gap-4 border p-5 transition-colors ${payment === "mercadopago" ? "border-volt bg-volt/5" : "border-white/15 hover:border-white/30"}`}>
                <input type="radio" name="payment" checked={payment === "mercadopago"} onChange={() => setPayment("mercadopago")} className="accent-volt" data-testid="checkout-payment-mercadopago-radio" />
                <div className="flex-1">
                  <p className="font-display text-lg font-black uppercase tracking-wide text-white">Mercado Pago</p>
                  <p className="text-xs text-zinc-400">Tarjetas, dinero en cuenta y hasta 3 cuotas</p>
                </div>
                <span className="font-mono text-xs font-bold text-mp">MP</span>
              </label>
              <label className={`flex cursor-pointer items-center gap-4 border p-5 transition-colors ${payment === "whatsapp" ? "border-wa bg-wa/5" : "border-white/15 hover:border-white/30"}`}>
                <input type="radio" name="payment" checked={payment === "whatsapp"} onChange={() => setPayment("whatsapp")} className="accent-wa" data-testid="checkout-payment-whatsapp-radio" />
                <div className="flex-1">
                  <p className="font-display text-lg font-black uppercase tracking-wide text-white">Coordinar por WhatsApp</p>
                  <p className="text-xs text-zinc-400">Te confirmamos el pago y la entrega por chat</p>
                </div>
                <MessageCircle className="h-5 w-5 text-wa" />
              </label>
            </div>
          </fieldset>
        </div>

        <aside className="lg:col-span-5">
          <div className="border border-white/10 bg-panel p-6 sm:p-8 lg:sticky lg:top-32">
            <p className="font-display text-xl font-black uppercase tracking-wide text-white">Tu pedido</p>
            <div className="mt-5 max-h-64 space-y-4 overflow-y-auto pr-1">
              {items.map((i) => (
                <div key={i.key} className="flex gap-3">
                  <img src={i.image} alt={i.name} className="h-14 w-14 shrink-0 object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-display text-sm font-bold uppercase text-white">{i.name}</p>
                    <p className="font-mono text-[11px] text-zinc-500">{i.qty}x {formatARS(i.price)}{i.size ? ` · ${i.size}` : ""}</p>
                  </div>
                  <p className="font-mono text-sm text-zinc-300">{formatARS(i.price * i.qty)}</p>
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-2 border-t border-white/10 pt-5">
              <div className="flex justify-between text-sm text-zinc-400">
                <span>Subtotal</span>
                <span className="font-mono" data-testid="checkout-subtotal">{formatARS(totals.subtotal)}</span>
              </div>
              {totals.discount > 0 && (
                <div className="flex justify-between font-mono text-sm font-bold text-volt" data-testid="checkout-discount-row">
                  <span>10% OFF 2da prenda</span>
                  <span>-{formatARS(totals.discount)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-white/10 pt-3 font-display text-2xl font-black uppercase text-white">
                <span>Total</span>
                <span className="font-mono" data-testid="checkout-total">{formatARS(totals.total)}</span>
              </div>
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="checkout-submit-button"
              className="mt-6 w-full bg-volt py-4 font-display text-base font-black uppercase tracking-widest text-black transition-colors hover:bg-volt-dim disabled:opacity-50"
            >
              {loading ? "Procesando..." : payment === "mercadopago" ? "Pagar con Mercado Pago" : "Enviar pedido"}
            </button>
            <p className="mt-3 text-center font-mono text-[10px] uppercase tracking-widest text-zinc-600">
              Compra protegida · Coordinamos envío al confirmar
            </p>
          </div>
        </aside>
      </form>
    </main>
  );
}
